// @flow
import React from "react";
import { nest, entries } from "d3-collection";
import Loadable from "react-loadable";
import { stack, stackOrderNone, stackOffsetExpand } from "d3-shape";
import { sum, ascending, extent } from "d3-array";
import { scaleOrdinal, scaleLinear, scaleBand } from "d3-scale";
import { format } from "d3-format";

/**
 * question (27 + 7)
 *   x all/country/division (1 + 7 + 6 = 14)
 *     x all/gender/rank/linemgr/los (5)
 * = 2380 charts?!
 * 
 * hierarchy w 2 levels:
 * - charts (e.g. q + country + facet)
 * - rows (facet value)
 */

/**
 *     {
    "GCRS_Division_Desc": "Wealth Management",
    "GCRS_Unit_Desc": "International Wealth Management",
    "GCRS_Area_Desc": "WM Europe and Emerging Markets",
    "Work_Location_Region": "SWITZERLAND",
    "Country_Descr": "Switzerland",
    "City": "Zürich",
    "Line_Manager_Flag": "N",
    "Being_with_UBS": "1 year or less",
    "Corporate_Title": "AD",
    "Gender": "Male",
    "dimension": "Career Path Planning & Internal Mobility",
    "subdimension": "Training and Development",
    "question": "to be encouraged and supported in pursuing my chosen career path",
    "questionCode": "A1_0",
    "likert": "Disagree",
    "ranking": "Priority 1"
  },
 */

type Datum = {
  Country_Descr: string,
  question: string,
  GCRS_Division_Desc: string,
  Gender: "Male" | "Female",
  likert: string,
  questionCode: string,
  subdimension: string
};

type Data = Array<Datum>;

type Facet =
  | "Countries"
  | "Business Divisions"
  | "Gender"
  | "Corporate Title"
  | "Being a Line Manager"
  | "Length of Service";

const COUNTRIES = new Map([
  ["Switzerland", 1],
  ["United Kingdom", 2],
  ["Poland", 3],
  ["United States", 4],
  ["Hong Kong", 5],
  ["Singapore", 6],
  ["India", 7]
]);

const SUBDIMENSIONS = new Map([
  ["Training and Development", 1],
  ["Internal Mobility", 2],
  ["Workplace", 3],
  ["Agile Working Practices", 4],
  ["Asset Management", 5],
  ["Feedback and Recognition", 6],
  ["Compensation and Benefits", 7],
  ["Collaboration and Interaction", 8],
  ["Management and Hierarchy", 9],
  ["Purpose and Meaning", 10]
]);

const DIVISIONS = new Map([
  ["Corporate Center", 1],
  ["Investment Bank", 2],
  ["Personal & Corporate Banking", 3],
  ["Wealth Management", 4],
  ["Wealth Management Americas", 5],
  ["Global Asset Management", 6]
]);

const FACET_ORDERS = new Map([
  // Corporate titles
  ["DI - MD", 1],
  ["AD", 2],
  ["AO", 3],
  ["EE", 4],
  // Countries
  ["Switzerland", 1],
  ["United Kingdom", 2],
  ["Poland", 3],
  ["United States", 4],
  ["Hong Kong", 5],
  ["Singapore", 6],
  ["India", 7]
]);

const facetKey = (facet: Facet) => d => {
  switch (facet) {
    case "Countries":
      return d.Country_Descr;
    case "Business Divisions":
      return d.GCRS_Division_Desc;
    case "Gender":
      return d.Gender;
    case "Corporate Title":
      return d.Corporate_Title;
    case "Being a Line Manager":
      return d.Line_Manager_Flag;
    case "Length of Service":
      return d.Being_with_UBS;
    default:
      // Match exhaustively: http://ouicar.github.io/2016/08/08/exhaustive-switch.html
      throw new Error("Unknown facet: " + facet);
  }
};

const fmtPct = format(".0%");

const filterValid = (d: Datum) =>
  COUNTRIES.has(d.Country_Descr) &&
  SUBDIMENSIONS.has(d.subdimension) &&
  DIVISIONS.has(d.GCRS_Division_Desc);

const LIKERT_NEUTRAL = 3;
const offsetLikert = (series, order) => {
  // normalize from 0 to 1
  stackOffsetExpand(series, order);

  if (!((n = series.length) > 1)) return;
  for (
    var i, j = 0, d, dy, yp, yn, n, m = series[order[0]].length;
    j < m;
    ++j
  ) {
    // Stack the neutral and positive values
    for (yp = yn = 0, i = LIKERT_NEUTRAL - 1; i < n; ++i) {
      dy = (d = series[order[i]][j])[1] - d[0];

      if (i === LIKERT_NEUTRAL - 1) {
        //neutral goes centered in the middle
        (d[0] = yp - dy / 2), (d[1] = yp += dy / 2);
      } else {
        (d[0] = yp), (d[1] = yp += dy);
      }
    }
    // Now stack the negative values
    for (yp = yn = 0, i = LIKERT_NEUTRAL - 1; i >= 0; --i) {
      dy = (d = series[order[i]][j])[1] - d[0];
      if (i === LIKERT_NEUTRAL - 1) {
        yp -= dy / 2;
      } else if (i < LIKERT_NEUTRAL - 1) {
        (d[1] = yp), (d[0] = yp -= dy);
      }
    }
  }
};

const likertStack = stack()
  .keys(["Strongly disagree", "Disagree", "Neutral", "Agree", "Strongly agree"])
  .value((d, key) => d.value[key])
  .order(stackOrderNone)
  .offset(offsetLikert);

const color = scaleOrdinal()
  .domain([
    "Strongly disagree",
    "Disagree",
    "Neutral",
    "Agree",
    "Strongly agree"
  ])
  .range(["#585148", "#807266", "#BEBEBE", "#D7C2AA", "#B89D83"]);

const priorityColor = scaleLinear().range(["#fff", "#3B8085"]);

const x = scaleLinear().domain([-1, 1]).rangeRound([100, 500]);

const rollupValues = values => ({
  "Strongly disagree": sum(values, d => d.likert === "Strongly disagree"),
  Disagree: sum(values, d => d.likert === "Disagree"),
  Neutral: sum(values, d => d.likert === "Neutral"),
  Agree: sum(values, d => d.likert === "Agree"),
  "Strongly agree": sum(values, d => d.likert === "Strongly agree"),
  "Priority 1": sum(values, d => d.ranking === "Priority 1") / values.length,
  "Priority 2": sum(values, d => d.ranking === "Priority 2") / values.length,
  "Priority 3": sum(values, d => d.ranking === "Priority 3") / values.length
});

const getKeyOrder = k => (FACET_ORDERS.has(k) ? FACET_ORDERS.get(k) : k);

export const InternalSurvey = Loadable({
  loader: () =>
    import(/* webpackChunkName: "data" */ "../../data/ubs-data.json"),
  loading: () => <div>…</div>,
  render: (data: Data, props) => {
    const bySubdimension = nest()
      .key(d => d.subdimension)
      .key(d => `... ${d.question}`)
      .key(() => "global")
      .rollup(rollupValues);

    const bySubdimensionFacets = nest()
      .key(d => `${d.subdimension}`)
      .key(d => `... ${d.question}`)
      .key(facetKey(props.facets))
      .sortKeys((a, b) => ascending(getKeyOrder(a), getKeyOrder(b)))
      .rollup(rollupValues);

    const subdimensionsGlobal = bySubdimension.object(data);
    const subdimensions = bySubdimensionFacets.entries(
      data.filter(filterValid)
    );
    return (
      <div className="page">
        <h1>{props.facets}</h1>
        {subdimensions.map(({ key: subdimension, values: questions }) => {
          return (
            <div className="subdimension">
              <h2>
                {subdimension}
              </h2>

              {questions.map(
                ({ key: question, values: facetsWithoutGlobal }, i) => {
                  const globalFacet = {
                    key: "Global",
                    value: subdimensionsGlobal[subdimension][question].global
                  };
                  const facets = [globalFacet, ...facetsWithoutGlobal];
                  const stack = likertStack(facets);
                  const facetKeys = facets.map(f => f.key);
                  const y = scaleBand()
                    .domain(facetKeys)
                    .range([60, facets.length * 30 + 60]);

                  return (
                    <div className="facet">
                      <div>
                        <h3>{question}</h3>
                      </div>
                      <div className="facetBlock">
                        <div className="questions">
                          {facetKeys.map((facetKey, i) =>
                            <div
                              className="question"
                              style={{
                                transform: `translate(${0}px,${y(facetKey) +
                                  y.step() / 2}px)`
                              }}
                            >
                              {facetKey}
                            </div>
                          )}
                        </div>
                        <svg
                          width={x.range()[1] + 100}
                          height={y.range()[1] + 50}
                        >
                          <g className="priority-label">
                            <text
                              dy="1.6em"
                              className="pcLabelTitle"
                              style={{ textAnchor: "end" }}
                              transform={`translate(${x(-1) - 4}, 30)`}
                            >
                              Disagree
                            </text>
                            <text
                              dy="1.6em"
                              className="pcLabelTitle"
                              style={{ textAnchor: "middle" }}
                              transform={`translate(${x(0)}, 30)`}
                            >
                              Neutral
                            </text>
                            <text
                              dy="1.6em"
                              className="pcLabelTitle"
                              style={{ textAnchor: "start" }}
                              transform={`translate(${x(1) + 4}, 30)`}
                            >
                              Agree
                            </text>
                          </g>

                          {[-1, -0.5, 0, 0.5, 1].map(d =>
                            <g transform={`translate(${x(d)},0)`}>
                              <path
                                className="tick"
                                d={`M0,${y.range()[0]}V${y.range()[1]}`}
                              />
                              <text
                                dy=".35em"
                                className="tickLabel"
                                transform={`translate(0,${y.range()[1] +
                                  y.step() / 2})`}
                              />
                            </g>
                          )}

                          {stack[0].map((d, i) =>
                            <g>
                              <text
                                dy=".35em"
                                className="rowLabel"
                                style={{ textAnchor: "end" }}
                                transform={`translate(${x(-1) - 4},${y(
                                  d.data.key
                                ) +
                                  y.step() / 2})`}
                              >
                                {fmtPct(
                                  stack[0][i][1] -
                                    stack[0][i][0] +
                                    (stack[1][i][1] - stack[1][i][0])
                                )}
                              </text>
                              <text
                                dy=".35em"
                                className="rowLabel"
                                style={{ textAnchor: "start" }}
                                transform={`translate(${x(1) + 4},${y(
                                  d.data.key
                                ) +
                                  y.step() / 2})`}
                              >
                                {fmtPct(
                                  stack[3][i][1] -
                                    stack[3][i][0] +
                                    (stack[4][i][1] - stack[4][i][0])
                                )}
                              </text>
                            </g>
                          )}

                          {stack.map(facet =>
                            <g fill={color(facet.key)}>
                              {facet.map((value, i) =>
                                <rect
                                  transform={`translate(${x(value[0])},${y(
                                    value.data.key
                                  ) + 3})`}
                                  height="24"
                                  width={x(value[1]) - x(value[0])}
                                />
                              )}
                            </g>
                          )}
                        </svg>
                        <svg width="130">
                          <g className="priority-label">
                            <text
                              dy="1.6em"
                              className="pcLabelTitle"
                              style={{ textAnchor: "middle" }}
                              transform={`translate(45, 12)`}
                            >
                              Priority
                            </text>
                            <text
                              dy="1.6em"
                              className="pcLabelTitle"
                              style={{ textAnchor: "middle" }}
                              transform={`translate(15, 30)`}
                            >
                              1
                            </text>
                            <text
                              dy="1.6em"
                              className="pcLabelTitle"
                              style={{ textAnchor: "middle" }}
                              transform={`translate(45, 30)`}
                            >
                              2
                            </text>
                            <text
                              dy="1.6em"
                              className="pcLabelTitle"
                              style={{ textAnchor: "middle" }}
                              transform={`translate(75, 30)`}
                            >
                              3
                            </text>
                          </g>
                          {facets.map(({ key: facet, value: answers }) => {
                            priorityColor.domain([0, 1]);
                            return (
                              <g>
                                <rect
                                  height="30"
                                  //width={`${answers['Priority 1']}`}
                                  width="30"
                                  fill={`${priorityColor(
                                    answers["Priority 1"]
                                  )}`}
                                  transform={`translate(0, ${y(facet)})`}
                                />
                                <text
                                  dy="1.6em"
                                  className="pcLabel"
                                  style={{ textAnchor: "middle" }}
                                  transform={`translate(15, ${y(facet)})`}
                                >
                                  {Math.round(answers["Priority 1"] * 100)}
                                </text>
                                <rect
                                  height="30"
                                  //width={`${answers['Priority 2']}`}
                                  width="30"
                                  fill={`${priorityColor(
                                    answers["Priority 2"]
                                  )}`}
                                  transform={`translate(30, ${y(facet)})`}
                                />
                                <text
                                  dy="1.6em"
                                  className="pcLabel"
                                  style={{ textAnchor: "middle" }}
                                  transform={`translate(45, ${y(facet)})`}
                                >
                                  {Math.round(answers["Priority 2"] * 100)}
                                </text>
                                <rect
                                  height="30"
                                  //width={`${answers['Priority 3']}`}
                                  width="30"
                                  fill={`${priorityColor(
                                    answers["Priority 3"]
                                  )}`}
                                  transform={`translate(60, ${y(facet)})`}
                                />
                                <text
                                  dy="1.6em"
                                  className="pcLabel"
                                  style={{ textAnchor: "middle" }}
                                  transform={`translate(75, ${y(facet)})`}
                                >
                                  {Math.round(answers["Priority 3"] * 100)}
                                </text>
                                <text
                                  dy="1.6em"
                                  className="pcLabel"
                                  style={{ textAnchor: "start" }}
                                  transform={`translate(92, ${y(facet)})`}
                                >
                                  %
                                </text>
                              </g>
                            );
                          })}
                        </svg>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          );
        })}
        <style jsx>{`
          div {
            color: #4D4D4D;
            font-family: FrutigerLTCom;
            font-weight: 300;
            font-size: 14px;
          }
          text {
            fill: #4D4D4D;
            font-family: FrutigerLTCom;
            font-weight: 300;
            font-size: 14px;
          }
          h1 {
            font-size: 48px;
            font-weight: 300;
            page-break-before: always;
          }
          h2 {
            font-size: 24px;
            font-weight: 300;
          }
          h3 {
            font-size: 16px;
            font-weight: 700;
            margin: 0;
          }
          svg {
            display: block;
          }
          .questionAndPriorities {
            display: flex;
            justify-content: space-between;
            width: 100%;
          }
          .priority-label {
            display: flex;
          }
          .tick {
            stroke-width: 1;
            stroke: #bbb;
            fill: none;
            shape-rendering: crispEdges;
          }
          .tickLabel {
            fill: #bbb;
            text-anchor: middle;
            font-size: 12px;
          }
          .rowLabel {
            fill: #4D4D4D;
            font-size: 12px;
            font-weight: 700;
          }
          .pcLabel {
            font-size: 12px;
            font-weight: 300;
          }
          .pcLabelTitle {
            font-size: 12px;
            font-weight: 700;
          }
          .facetBlock {
            display: flex;
            justify-content: space-between;
            width: 1000px;
          }
          .questions {
            width: 200px;
            position: relative;
          }
          .question {
            position: absolute;
            width: 100%;

            font-size: 12px;
            height: 30px;
            margin-top: -15px;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
          .subdimension {
            page-break-before: always;
          }
        `}</style>
      </div>
    );
  }
});

export default InternalSurvey;



// WEBPACK FOOTER //
// ./src/components/InternalSurvey/InternalSurvey.js